import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { SearchBar, BuyMinutesModal, useToast } from "../../components";
import { wallet } from "../../store";
import "./wallet.css";
import axios from "../../axios";

const Wallet = () => {
  const dispatch = useDispatch();
  const { balance, monthlyUsage, transactions, pagination, isLoading } =
    useSelector((state) => state.wallet);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toast = useToast();
  const hasFetched = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-invoking effects in development,
    // which would otherwise fire these API calls twice on mount.
    if (hasFetched.current) return;
    hasFetched.current = true;

    dispatch(wallet.fetchWalletBalance());
    dispatch(wallet.fetchTransactionHistory({ page: 1, limit: 10 }));
  }, [dispatch]);

  const handleBuyMinutesClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmitMinutes = async (minutes) => {
    try {
      // Step 1: Create order on backend
      const orderResponse = await axios.post("/payments/create-order", {
        minutes,
      });

      if (orderResponse.status !== 200) {
        toast.error("Failed to create order. Please try again.");
        return;
      }

      const order = orderResponse.data.data;

      // Step 2: Show Razorpay payment popup
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID, // Replace with your Razorpay Key ID
        amount: order.amount,
        currency: order.currency,
        order_id: order.id,
        handler: async (response) => {
          try {
            // Step 3: Verify payment on backend
            const verifyResponse = await axios.post(
              "/payments/verify-payment",
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
            );

            if (verifyResponse.status === 200) {
              toast.success(
                "Payment successful! Minutes added to your wallet.",
              );
              // Update wallet balance
              dispatch(wallet.fetchWalletBalance());
              dispatch(wallet.fetchTransactionHistory({ page: 1, limit: 10 }));
              setIsModalOpen(false);
            } else {
              toast.error("Payment verification failed. Please try again.");
            }
          } catch (verifyError) {
            console.error("Payment verification error:", verifyError);
            toast.error("Payment verification failed. Please try again.");
          }
        },
        theme: {
          color: "#007bff",
        },
      };

      if (!window.Razorpay) {
        throw new Error("Razorpay checkout script not loaded.");
      }

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Payment error:", error);
      if (error.response && error.response.data) {
        toast.error(
          error.response.data.message || "Payment failed. Please try again.",
        );
      } else {
        toast.error("Payment failed. Please try again.");
      }
    }
  };

  const handleLoadMore = () => {
    const nextPage = pagination.page + 1;
    dispatch(wallet.fetchTransactionHistory({ page: nextPage, limit: 10 }));
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Format time for display
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="wallet-page">
      <SearchBar />

      <div className="wallet-content">
        <div className="wallet-header">
          <h1>My Wallet</h1>
        </div>

        {/* Balance and Monthly Usage Row */}
        <div className="wallet-cards-row">
          {/* Balance Card */}
          {isLoading && !balance ? (
            <div className="skeleton-balance-card">
              <div className="skeleton-balance-top">
                <div className="skeleton-circle skeleton" />
                <div className="skeleton-line skeleton-line-sm skeleton" />
              </div>
              <div className="skeleton-line skeleton-line-lg skeleton" />
              <div className="skeleton-line skeleton-line-md skeleton" />
              <div className="skeleton-line skeleton-line-btn skeleton" />
            </div>
          ) : (
            <div className="balance-card">
              <div className="balance-card-top">
                <div className="balance-icon">
                  <span className="material-symbols-outlined">
                    account_balance_wallet
                  </span>
                </div>
                <div className="balance-label">Available Balance</div>
              </div>
              <div className="balance-value">
                {balance?.balance?.value || 0}
                <span className="balance-unit"> Minutes</span>
              </div>
              <div className="balance-description">
                Equivalent to roughly {balance?.estimatedSessions || 0} full
                coaching sessions
              </div>
              <button
                className="balance-buy-btn"
                onClick={handleBuyMinutesClick}
              >
                <span className="material-symbols-outlined">add</span>
                Buy More Minutes
              </button>
            </div>
          )}

          {/* Monthly Usage Card */}
          {isLoading && !balance ? (
            <div className="monthly-usage-card">
              <div className="skeleton-line skeleton-line-sm skeleton" />
              <div className="skeleton-line skeleton-line-lg skeleton" />
              <div className="skeleton-line skeleton-line-sm skeleton" />
            </div>
          ) : (
            <div className="monthly-usage-card">
              <div className="monthly-usage-label">Monthly Usage</div>
              <div className="monthly-usage-value">
                {monthlyUsage}
                <span className="monthly-usage-unit"> min</span>
              </div>
              <div className="monthly-usage-period">This month</div>
            </div>
          )}
        </div>

        {/* How Minutes Work Card */}
        <div className="how-minutes-work-card">
          <div className="how-minutes-work-header">
            <span className="material-symbols-outlined">info</span>
            <h3>How minutes work</h3>
          </div>
          <div className="how-minutes-work-content">
            <p>
              Minutes are deducted only when you start a live session with your
              coach. Cancellation fees apply if canceled within 2 hours.
            </p>
          </div>
        </div>

        {/* Transaction History Section */}
        <div className="wallet-transaction-section">
          <div className="transaction-header">
            <h2>Transaction History</h2>
          </div>

          <div className="transaction-list">
            {isLoading && transactions.length === 0 ? (
              <>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="skeleton-transaction-item">
                    <div className="skeleton-transaction-info">
                      <div className="skeleton-transaction-icon skeleton" />
                      <div className="skeleton-transaction-details">
                        <div className="skeleton-transaction-line skeleton-transaction-line-title skeleton" />
                        <div className="skeleton-transaction-line skeleton-transaction-line-date skeleton" />
                      </div>
                    </div>
                    <div className="skeleton-transaction-amount skeleton" />
                  </div>
                ))}
              </>
            ) : transactions && transactions.length > 0 ? (
              transactions.map((transaction) => (
                <div key={transaction._id} className="transaction-item">
                  <div className="transaction-info">
                    <div className="transaction-icon">
                      <span className="material-symbols-outlined">
                        {transaction.amount > 0
                          ? "add_circle"
                          : "remove_circle"}
                      </span>
                    </div>
                    <div className="transaction-details">
                      <p className="transaction-description">
                        {transaction.description || "Wallet Transaction"}
                      </p>
                      <p className="transaction-date">
                        {formatDate(transaction.date)} at{" "}
                        {formatTime(transaction.date)}
                      </p>
                    </div>
                  </div>
                  <div className="transaction-amount">
                    <span
                      className={transaction.amount > 0 ? "credit" : "debit"}
                    >
                      {transaction.amount > 0 ? "+" : ""}
                      {transaction.minutes} min
                    </span>
                    {transaction.last4 && (
                      <p className="transaction-card">
                        •••• {transaction.last4}
                      </p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "48px", color: "#94a3b8" }}
                >
                  receipt_long
                </span>
                <h3>No Transactions Yet</h3>
                <p>Your transaction history will appear here</p>
              </div>
            )}

            {/* Load More */}
            {pagination && pagination.page < pagination.pages && (
              <div className="load-more-container">
                <button
                  className="load-more-btn"
                  onClick={handleLoadMore}
                  disabled={isLoading}
                >
                  {isLoading ? "Loading..." : "Load More"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Buy Minutes Modal */}
        <BuyMinutesModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmitMinutes}
        />
      </div>
    </div>
  );
};

export default Wallet;
